import { F0Dialog, f0FormField, useF0Form, useF0FormDefinition } from '@factorialco/f0-react'
import {
  EditProcurementTaskFormDocument,
  TaskFormsDataCollectionDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { FactorialF0Form } from 'modules/core/components/FactorialF0Form'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  taskFormId: string
  taskFormName: string
  taskFormCategory: 'basic' | 'provider'
  onClose: () => void
}

type CategoryValue = 'basic' | 'provider'

const categoryOptions = (showProvider: boolean) => {
  const options: Array<{ label: string; value: CategoryValue }> = [
    {
      label: i18n.t('procurement.purchase_requests.settings.task_forms.form_category_value.basic'),
      value: 'basic',
    },
  ]

  if (showProvider) {
    options.push({
      label: i18n.t(
        'procurement.purchase_requests.settings.task_forms.form_category_value.provider'
      ),
      value: 'provider',
    })
  }

  return options
}

const EditTaskFormModal = ({ taskFormId, taskFormName, taskFormCategory, onClose }: Props) => {
  const { has_dev_procurement_vendor_onboarding } = useFeatures([
    'dev_procurement_vendor_onboarding',
  ])

  const [editProcurementTaskForm] = useMutation(EditProcurementTaskFormDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementTaskForm,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const showCategorySelector = has_dev_procurement_vendor_onboarding

  const schema = z.object({
    name: f0FormField(z.string().trim().min(1), {
      label: i18n.t('procurement.purchase_requests.settings.task_forms.create.labels.name'),
    }),
    ...(showCategorySelector && {
      category: f0FormField(z.enum(['basic', 'provider']), {
        label: i18n.t('procurement.purchase_requests.settings.task_forms.create.labels.category'),
        fieldType: 'select',
        options: categoryOptions(has_dev_procurement_vendor_onboarding),
      }),
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'edit-task-form',
    schema,
    defaultValues: {
      name: taskFormName,
      category: taskFormCategory,
    },
    onSubmit: async ({ data }) => {
      const rawCategory = 'category' in data ? data.category : undefined
      const category: CategoryValue | undefined =
        rawCategory === 'provider' ? 'provider' : rawCategory === 'basic' ? 'basic' : undefined

      const result = await editProcurementTaskForm({
        variables: {
          id: taskFormId,
          name: data.name,
          ...(category && { category }),
        },
        refetchQueries: [TaskFormsDataCollectionDocument],
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: fieldErrors }
      }

      onClose()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.settings.task_forms.edit.title')}
      description={i18n.t('procurement.purchase_requests.settings.task_forms.edit.description')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.edit.buttons.save'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.edit.buttons.cancel'),
        onClick: onClose,
      }}
    >
      <FactorialF0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}

export default EditTaskFormModal
